import { Box, HStack, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  ValleyBankLoanAccountWithCollateralStats,
  parse,
} from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankUnderMarginLoanAccountsQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  Alert,
  ProgressAlert,
} from 'packages/common-frontend/components/Alert';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from 'packages/common-frontend/components/icons';
import { InfoCard } from 'packages/common-frontend/components/InfoCard';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { formatCurrencyAmount } from 'packages/common-frontend/utils';
import { LoanAccountsOverviewTemplate } from 'valleyBank/domains/LoanAccounts/ui/Overview/LoanAccountsOverviewTemplate';
import {
  LoanAccountsTableContainer,
  useLoanAccountsBaseColumns,
} from 'valleyBank/domains/LoanAccounts/ui/Overview/LoanAccountsTable';
import { useDataMetadataContext } from 'valleyBank/domains/page/hooks/DataMetadataContext';

import 'source/borrower/borrower.scss';

export const LoanAccountsOverviewUnderMarginReportRouter = () => {
  const toast = useToast();
  const history = useHistory();
  const { reportingDate, isPreview } = useDataMetadataContext();
  const [isLoading, setIsLoading] = useState(true);
  const [loanAccounts, setLoanAccounts] = useState<
    ValleyBankLoanAccountWithCollateralStats[]
  >([]);

  let routeLevelError = false;

  const { error } = useGetValleyBankUnderMarginLoanAccountsQuery({
    variables: {
      reportingDate,
      isPreview,
    },
    onCompleted: (data) => {
      if (data.valleyBankUnderMarginLoanAccounts) {
        try {
          const newLoanAccounts = parse.parseValleyBankLoanAccountWithCollateralStatsArray(
            data.valleyBankUnderMarginLoanAccounts,
          );
          const sortedLoanAccounts = newLoanAccounts.sort(
            (a, b) => a.marginAvailable - b.marginAvailable,
          );
          setLoanAccounts(sortedLoanAccounts);
        } catch (err) {
          Sentry.captureException(err);
        } finally {
          setIsLoading(false);
        }
      }
    },
    onError: () => {
      if (!routeLevelError && !toast.isActive(errorToastId)) {
        errorToastConfig.title = `Failed to load loan accounts under margin report page`;
        routeLevelError = true;
        toast(errorToastConfig);
        history.push('/');
      }
    },
  });

  const columns = useLoanAccountsBaseColumns();

  const table = useReactTable({
    data: loanAccounts,
    columns,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const tableDataForDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  const totalUnderMargin = loanAccounts.reduce(
    (acc, { marginAvailable }) => acc + marginAvailable,
    0,
  );

  const totalLoanAccountsUnderMargin = loanAccounts.length;

  const averageUnderMargin = commonUtils.divideByOrZero(
    totalUnderMargin,
    totalLoanAccountsUnderMargin,
  );

  return (
    <Box padding={dashboardRoutePadding}>
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load loan account under margin report page`;
            routeLevelError = true;
            toast(errorToastConfig);
            history.push('/');
          }
        }}
      >
        <LoanAccountsOverviewTemplate selectedTab="underMarginReport">
          {isLoading && (
            <ProgressAlert>Loading under margin report</ProgressAlert>
          )}
          {!isLoading && loanAccounts.length === 0 && (
            <Alert status="info">
              <Text>No loan accounts under margin found</Text>
            </Alert>
          )}
          {!isLoading && loanAccounts.length > 0 && (
            <>
              <HStack w="100%" spacing="24px" p="40px 0">
                <InfoCard
                  title="Total Under Margin"
                  value={formatCurrencyAmount(
                    totalUnderMargin,
                    Bank.Currency.USD,
                  )}
                  icon={<Icon icon="dollar" />}
                  flexGrow={1}
                />
                <InfoCard
                  title="Number of Loan Accounts Under Margin"
                  value={totalLoanAccountsUnderMargin.toString()}
                  icon={<Icon icon="hashtag" />}
                  flexGrow={1}
                />
                <InfoCard
                  title="Average Under Margin"
                  value={formatCurrencyAmount(
                    averageUnderMargin,
                    Bank.Currency.USD,
                  )}
                  icon={<Icon icon="dollar" />}
                  flexGrow={1}
                />
              </HStack>
              <HStack w="100%" justify="flex-end">
                <CsvXlsxDownloadSelect
                  dataRows={tableDataForDownload.dataRows}
                  headers={tableDataForDownload.headers}
                  filenamePrefix="loan-accounts-under-margin-report"
                />
              </HStack>
              <LoanAccountsTableContainer
                table={table}
                isLoading={isLoading}
                isError={Boolean(error)}
              />
              <TanStackPaginationControl table={table} />
            </>
          )}
        </LoanAccountsOverviewTemplate>
      </Sentry.ErrorBoundary>
    </Box>
  );
};
